import { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Mail, Copy, RefreshCw, Clock, Inbox, Check, AlertCircle, Loader2, Eye, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface EmailMessage {
  id: string;
  from: {
    address: string;
    name: string;
  };
  subject: string;
  intro: string;
  createdAt: string;
  hasAttachments: boolean;
  isRead?: boolean;
  isWelcome?: boolean;
}

interface EmailContent {
  id: string;
  from: {
    address: string;
    name: string;
  };
  subject: string;
  intro: string;
  text: string;
  html: string[];
  createdAt: string;
}

const API_BASE = 'https://api.mail.tm';
const COOLDOWN_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

// Welcome email template
const createWelcomeEmail = (emailAddress: string): EmailMessage => ({
  id: 'welcome-email',
  from: {
    address: 'admin@tam27.store',
    name: 'TAM Store',
  },
  subject: 'Welcome to TAM Temp Mail! 🎉',
  intro: 'Welcome to TAM Store\'s Temporary Email Service!',
  createdAt: new Date().toISOString(),
  hasAttachments: false,
  isRead: false,
  isWelcome: true,
});

const welcomeEmailContent: EmailContent = {
  id: 'welcome-email',
  from: {
    address: 'admin@tam27.store',
    name: 'TAM Store',
  },
  subject: 'Welcome to TAM Temp Mail! 🎉',
  intro: 'Welcome to TAM Store\'s Temporary Email Service!',
  text: `Welcome to TAM Store's Temporary Email Service!

Your temporary email address is ready to use.
This email will expire in 10 minutes.

Tips:
- Use this email for signups and verifications
- All incoming emails appear instantly
- Copy your email with one click

Need help? Contact support@tam27.store

— TAM Store Team`,
  html: [`<div style="font-family: sans-serif; padding: 20px; max-width: 600px; margin: 0 auto;">
    <h2 style="color: #10b981;">Welcome to TAM Store's Temporary Email Service! 🎉</h2>
    <p>Your temporary email address is ready to use.</p>
    <p><strong>This email will expire in 10 minutes.</strong></p>
    <h3 style="margin-top: 24px;">Tips:</h3>
    <ul>
      <li>Use this email for signups and verifications</li>
      <li>All incoming emails appear instantly</li>
      <li>Copy your email with one click</li>
    </ul>
    <p style="margin-top: 24px; color: #666;">Need help? Contact <a href="mailto:support@tam27.store">support@tam27.store</a></p>
    <p style="margin-top: 24px; color: #888;">— TAM Store Team</p>
  </div>`],
  createdAt: new Date().toISOString(),
};

// Avatar colors for sender initials
const avatarColors = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500',
];

const getAvatarColor = (email: string) => {
  const index = email.charCodeAt(0) % avatarColors.length;
  return avatarColors[index];
};

const getInitial = (name: string | undefined, email: string) => {
  if (name && name.length > 0) return name.charAt(0).toUpperCase();
  return email.charAt(0).toUpperCase();
};

const TempMail = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [readEmails, setReadEmails] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailContent | null>(null);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('tempmail_sound') !== 'false';
  });
  
  // Cooldown state
  const [cooldownEnd, setCooldownEnd] = useState<number | null>(() => {
    const saved = localStorage.getItem('tempmail_cooldown_end');
    if (saved) {
      const end = parseInt(saved, 10);
      if (end > Date.now()) return end;
    }
    return null;
  });
  const [cooldownLeft, setCooldownLeft] = useState(0);
  
  const prevEmailCount = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBJD/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBJD/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA');
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Load from sessionStorage on mount
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('tempmail_email');
    const savedToken = sessionStorage.getItem('tempmail_token');
    const savedAccountId = sessionStorage.getItem('tempmail_accountId');
    const savedExpiry = sessionStorage.getItem('tempmail_expiry');
    const savedReadEmails = sessionStorage.getItem('tempmail_read_emails');
    
    if (savedReadEmails) {
      try {
        setReadEmails(new Set(JSON.parse(savedReadEmails)));
      } catch (e) {
        console.warn('Failed to parse saved read emails:', e);
      }
    }
    
    if (savedEmail && savedToken && savedAccountId && savedExpiry) {
      const expiryTime = parseInt(savedExpiry, 10);
      const now = Date.now();
      const remaining = Math.floor((expiryTime - now) / 1000);
      
      if (remaining > 0) {
        setEmail(savedEmail);
        setToken(savedToken);
        setAccountId(savedAccountId);
        setTimeLeft(remaining);
        // Add welcome email back
        setEmails([createWelcomeEmail(savedEmail)]);
      } else {
        clearSessionStorage();
      }
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (!cooldownEnd) {
      setCooldownLeft(0);
      return;
    }

    const updateCooldown = () => {
      const remaining = cooldownEnd - Date.now();
      if (remaining <= 0) {
        setCooldownEnd(null);
        setCooldownLeft(0);
        localStorage.removeItem('tempmail_cooldown_end');
      } else {
        setCooldownLeft(Math.ceil(remaining / 1000));
      }
    };

    updateCooldown();
    const timer = setInterval(updateCooldown, 1000);
    return () => clearInterval(timer);
  }, [cooldownEnd]);

  const clearSessionStorage = () => {
    sessionStorage.removeItem('tempmail_email');
    sessionStorage.removeItem('tempmail_token');
    sessionStorage.removeItem('tempmail_accountId');
    sessionStorage.removeItem('tempmail_expiry');
    sessionStorage.removeItem('tempmail_read_emails');
  };

  // Save read emails to sessionStorage
  useEffect(() => {
    if (readEmails.size > 0) {
      sessionStorage.setItem('tempmail_read_emails', JSON.stringify([...readEmails]));
    }
  }, [readEmails]);

  // Countdown timer
  useEffect(() => {
    if (!email || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setEmail(null);
          setToken(null);
          setAccountId(null);
          setEmails([]);
          clearSessionStorage();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, timeLeft]);

  // Auto-refresh inbox every 10 seconds
  useEffect(() => {
    if (!token) return;

    const refreshInterval = setInterval(() => {
      fetchInbox();
    }, 10000);

    fetchInbox();

    return () => clearInterval(refreshInterval);
  }, [token]);

  // Check for new emails and play sound
  useEffect(() => {
    const realEmails = emails.filter(e => !e.isWelcome);
    if (realEmails.length > prevEmailCount.current && prevEmailCount.current > 0) {
      playNotificationSound();
      toast.success('New email received!');
    }
    prevEmailCount.current = realEmails.length;
  }, [emails, playNotificationSound]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const generateRandomString = (length: number) => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const generateEmail = async () => {
    if (!user) {
      toast.error('Please sign in to use this tool');
      return;
    }

    // Check cooldown
    if (cooldownEnd && cooldownEnd > Date.now()) {
      toast.error('Cooldown active', {
        description: `Please wait ${formatTime(cooldownLeft)} before generating another email.`,
      });
      return;
    }

    setIsLoading(true);
    setApiError(false);
    
    try {
      // Step 1: Get available domains
      const domainsResponse = await fetch(`${API_BASE}/domains`);
      
      if (!domainsResponse.ok) {
        throw new Error('Failed to fetch domains');
      }
      
      const domainsData = await domainsResponse.json();
      const domains = domainsData['hydra:member'];
      
      if (!domains || domains.length === 0) {
        throw new Error('No domains available');
      }

      const selectedDomain = domains[0].domain;
      const username = generateRandomString(10);
      const password = generateRandomString(16);
      const emailAddress = `${username}@${selectedDomain}`;

      // Step 2: Create account
      const createResponse = await fetch(`${API_BASE}/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: emailAddress,
          password: password,
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        console.error('Account creation error:', errorData);
        throw new Error('Failed to create email account');
      }

      const accountData = await createResponse.json();

      // Step 3: Get authentication token
      const tokenResponse = await fetch(`${API_BASE}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: emailAddress,
          password: password,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to get authentication token');
      }

      const tokenData = await tokenResponse.json();
      const authToken = tokenData.token;

      // Save to history
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();
      await supabase.from('temp_mail_history').insert({
        user_id: user.id,
        email_address: emailAddress,
        session_id: accountData.id,
        expires_at: expiresAt,
      });

      // Log tool usage
      await supabase.from('tool_usage').insert({
        user_id: user.id,
        tool_name: 'temp_mail',
      });

      // Set cooldown
      const newCooldownEnd = Date.now() + COOLDOWN_DURATION;
      setCooldownEnd(newCooldownEnd);
      localStorage.setItem('tempmail_cooldown_end', newCooldownEnd.toString());

      // Save to sessionStorage
      const expiryTime = Date.now() + 10 * 60 * 1000;
      sessionStorage.setItem('tempmail_email', emailAddress);
      sessionStorage.setItem('tempmail_token', authToken);
      sessionStorage.setItem('tempmail_accountId', accountData.id);
      sessionStorage.setItem('tempmail_expiry', expiryTime.toString());

      setEmail(emailAddress);
      setToken(authToken);
      setAccountId(accountData.id);
      setTimeLeft(600);
      setReadEmails(new Set());
      
      // Add welcome email
      setEmails([createWelcomeEmail(emailAddress)]);
      prevEmailCount.current = 0;
      
      toast.success('Temporary email generated!');
    } catch (error) {
      console.error('Error generating email:', error);
      setApiError(true);
      toast.error('Service unavailable. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInbox = async () => {
    if (!token) return;

    setIsRefreshing(true);
    try {
      const response = await fetch(`${API_BASE}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setEmail(null);
          setToken(null);
          setAccountId(null);
          setEmails([]);
          clearSessionStorage();
          toast.error('Session expired. Please generate a new email.');
          return;
        }
        throw new Error('Failed to fetch inbox');
      }
      
      const data = await response.json();
      const messages = (data['hydra:member'] || []) as EmailMessage[];
      
      // Add welcome email at the end if we have an email
      if (email) {
        const welcomeEmail = createWelcomeEmail(email);
        welcomeEmail.isRead = readEmails.has('welcome-email');
        setEmails([...messages, welcomeEmail]);
      } else {
        setEmails(messages);
      }
      setApiError(false);
    } catch (error) {
      console.error('Error fetching inbox:', error);
      setApiError(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const readEmail = async (messageId: string) => {
    // Mark as read
    setReadEmails(prev => new Set([...prev, messageId]));
    
    if (messageId === 'welcome-email') {
      setSelectedEmail(welcomeEmailContent);
      return;
    }

    if (!token) return;

    setIsLoadingEmail(true);
    try {
      const response = await fetch(`${API_BASE}/messages/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to read email');
      }
      
      const data = await response.json();
      setSelectedEmail(data);
    } catch (error) {
      console.error('Error reading email:', error);
      toast.error('Failed to load email content');
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const deleteEmail = (messageId: string) => {
    setEmails(prev => prev.filter(e => e.id !== messageId));
    toast.success('Email deleted');
  };

  const copyEmail = () => {
    if (email) {
      navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success('Email copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('tempmail_sound', newValue.toString());
    toast.success(newValue ? 'Sound notifications enabled' : 'Sound notifications disabled');
  };

  const formatEmailDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const isOnCooldown = cooldownEnd !== null && cooldownEnd > Date.now();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/20">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">TAM Temp Mail</h1>
            <p className="text-muted-foreground">
              Generate temporary email addresses • Free to use
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSound}
          title={soundEnabled ? 'Disable sound notifications' : 'Enable sound notifications'}
        >
          {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </Button>
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Service temporarily unavailable. Please try again later.</span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Email Generator Card */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Temporary Email</span>
              {email && (
                <Badge variant="secondary" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {formatTime(timeLeft)}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!email ? (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Generate a temporary email address that lasts for 10 minutes
                </p>
                {isOnCooldown && (
                  <p className="text-sm text-amber-500 mb-4">
                    New email available in {formatTime(cooldownLeft)}
                  </p>
                )}
                <Button onClick={generateEmail} disabled={isLoading || isOnCooldown} className="gap-2">
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  {isOnCooldown ? `Wait ${formatTime(cooldownLeft)}` : 'Generate Email'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="font-mono text-sm flex-1 truncate">{email}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={copyEmail}
                    className="flex-shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={fetchInbox}
                    disabled={isRefreshing}
                    className="flex-1 gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh Inbox
                  </Button>
                  <Button 
                    onClick={generateEmail} 
                    disabled={isLoading || isOnCooldown} 
                    className="gap-2"
                    title={isOnCooldown ? `Wait ${formatTime(cooldownLeft)}` : 'Generate new email'}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    {isOnCooldown ? formatTime(cooldownLeft) : 'New Email'}
                  </Button>
                </div>
                
                {isOnCooldown && (
                  <p className="text-xs text-center text-muted-foreground">
                    Cooldown: New email available in {formatTime(cooldownLeft)}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inbox Card */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" />
              Inbox
              {emails.length > 0 && (
                <Badge variant="default">{emails.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!email ? (
              <div className="text-center py-8 text-muted-foreground">
                <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Generate an email to view inbox</p>
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No emails yet</p>
                <p className="text-sm">Inbox refreshes every 10 seconds</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {emails.map((msg, index) => {
                    const isRead = readEmails.has(msg.id);
                    const senderEmail = msg.from?.address || 'unknown';
                    
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          "p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all duration-300",
                          !isRead && "bg-primary/5 border-primary/20",
                          msg.isWelcome && "bg-green-500/10 border-green-500/20"
                        )}
                        style={{ 
                          animationDelay: `${index * 50}ms`,
                          animation: 'slide-in 0.3s ease-out forwards'
                        }}
                        onClick={() => readEmail(msg.id)}
                      >
                        <div className="flex items-start gap-3">
                          {/* Avatar */}
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0",
                            msg.isWelcome ? "bg-green-500" : getAvatarColor(senderEmail)
                          )}>
                            {getInitial(msg.from?.name, senderEmail)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {/* Unread indicator */}
                              {!isRead && (
                                <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                              )}
                              <p className={cn(
                                "font-medium text-sm truncate",
                                !isRead && "font-semibold"
                              )}>
                                {msg.subject || '(No Subject)'}
                              </p>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              From: {msg.from?.name || senderEmail}
                            </p>
                            <p className="text-xs text-muted-foreground/70 truncate mt-1">
                              {msg.intro?.substring(0, 50) || 'No preview available'}...
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatEmailDate(msg.createdAt)}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            {!msg.isWelcome && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteEmail(msg.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Content Dialog */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] glass">
          <DialogHeader className="border-b border-border/50 pb-4">
            <DialogTitle className="text-xl pr-8">{selectedEmail?.subject || '(No Subject)'}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0",
                    selectedEmail?.id === 'welcome-email' ? "bg-green-500" : getAvatarColor(selectedEmail?.from?.address || '')
                  )}>
                    {getInitial(selectedEmail?.from?.name, selectedEmail?.from?.address || '')}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {selectedEmail?.from?.name || selectedEmail?.from?.address || 'Unknown'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {selectedEmail?.from?.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>To: {email}</span>
                  <span>•</span>
                  <span>{selectedEmail && formatEmailDate(selectedEmail.createdAt)}</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh] mt-4">
            {isLoadingEmail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="prose prose-sm dark:prose-invert max-w-none p-4 bg-card/50 rounded-lg border border-border/30">
                {selectedEmail?.html && selectedEmail.html.length > 0 ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.html.join(''), { FORBID_TAGS: ['script', 'style'], FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'] }) }} />
                ) : selectedEmail?.text ? (
                  <pre className="whitespace-pre-wrap font-sans text-foreground">{selectedEmail.text}</pre>
                ) : (
                  <p className="text-muted-foreground">No content available</p>
                )}
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t border-border/50">
            <Button variant="outline" onClick={() => setSelectedEmail(null)}>
              Close
            </Button>
            {selectedEmail && selectedEmail.id !== 'welcome-email' && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  deleteEmail(selectedEmail.id);
                  setSelectedEmail(null);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CSS for slide-in animation */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TempMail;